import React, { useMemo } from 'react';
import { FileText, ShoppingCart, Wrench, Package } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useQuery } from '@tanstack/react-query';
import { dashboardService, type ActivityItem as ServiceActivityItem } from '../../../services/dashboardService';
import { useLanguage } from '../../../features/language/useLanguage';

interface ActivityItem {
  id: string;
  type: 'sales_order' | 'purchase_order' | 'work_order' | 'stock_movement' | 'other';
  title: string;
  description: string;
  createdAt: string;
}

export const ActivityFeed: React.FC = () => {
  const { t } = useLanguage();

  const { data: rawActivities, isLoading: loading } = useQuery<ServiceActivityItem[]>({
    queryKey: ['activity-feed'],
    queryFn: () => dashboardService.getActivityFeed(),
  });

  const activities: ActivityItem[] = useMemo(() => {
    if (!rawActivities) return [];
    return rawActivities.map((item: ServiceActivityItem) => {
      let type: ActivityItem['type'] = 'other';
      let title = t('dashboard.activityFeed.systemActivity');

      if (item.type === 'sale' || item.type === 'sales_order') {
        type = 'sales_order';
        title = t('dashboard.activityFeed.salesOrder');
      } else if (item.type === 'purchase' || item.type === 'purchase_order') {
        type = 'purchase_order';
        title = t('dashboard.activityFeed.purchaseOrder');
      } else if (item.type === 'work_order') {
        type = 'work_order';
        title = t('dashboard.activityFeed.workOrder');
      } else if (item.type === 'stock_movement') {
        type = 'stock_movement';
        title = t('dashboard.activityFeed.stockMovement');
      }

      return {
        id: item.id,
        type,
        title,
        description: item.description,
        createdAt: item.timestamp,
      };
    });
  }, [rawActivities, t]);

  const getIcon = (type: ActivityItem['type']) => {
    switch (type) {
      case 'sales_order':
        return <FileText className="h-5 w-5 text-green-500" />;
      case 'purchase_order':
        return <ShoppingCart className="h-5 w-5 text-red-500" />;
      case 'work_order':
        return <Wrench className="h-5 w-5 text-purple-500" />;
      case 'stock_movement':
        return <Package className="h-5 w-5 text-green-500" />;
      default:
        return <FileText className="h-5 w-5 text-gray-500" />;
    }
  };

  if (loading) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">{t('dashboard.activityFeed.title')}</h3>
        <div className="animate-pulse space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-10 bg-gray-100 rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg flex flex-col">
      <div className="p-6 flex flex-col">
        <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4 shrink-0">
          {t('dashboard.activityFeed.title')}
        </h3>
        <div className="max-h-[480px] overflow-y-auto pr-2">
          <div>
            <ul className="-mb-8">
              {activities.map((activity, activityIdx) => (
                <li key={activity.id}>
                  <div className="relative pb-8">
                    {activityIdx !== activities.length - 1 ? (
                      <span
                        className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200"
                        aria-hidden="true"
                      />
                    ) : null}
                    <div className="relative flex space-x-3">
                      <div className="bg-white rounded-full flex items-center justify-center ring-8 ring-white">
                        {getIcon(activity.type)}
                      </div>
                      <div className="min-w-0 flex-1 pt-1.5 flex justify-between space-x-4">
                        <div>
                          <p className="text-sm text-gray-500">
                            <span className="font-medium text-gray-900 block mb-0.5">{activity.title}</span>
                            {activity.description}
                          </p>
                        </div>
                        <div className="text-right text-sm whitespace-nowrap text-gray-500">
                          <time dateTime={activity.createdAt}>
                            {formatDistanceToNow(new Date(activity.createdAt), { addSuffix: true })}
                          </time>
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};
